import "./styles/index.css";
import { FC, Fragment, useEffect, useRef, useCallback, useState } from "react";
import Modal from "./components/Modal";
import { ArrowSmallLeftIcon } from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

// Helper function for URL search params
export const getNewParam = ({
  paramName = "photoId",
  value,
}: {
  paramName?: string;
  value: string | number;
}) => {
  const params = new URLSearchParams(document.location.search);
  params.set(paramName, String(value));
  return params.toString();
};

// Define the Image interface
interface Image {
  id: number;
  url: string;
}

// Define the Props interface for the main component
interface Props {
  onClose?: () => void;
  isShowModal: boolean;
  listingImgs?: any;
}

// Custom hook to manage last viewed photo with types defined here
const useLastViewedPhoto = (): [string | null, (value: string | null) => void] => {
  const [lastViewedPhoto, setLastViewedPhoto] = useState<string | null>(null);
  return [lastViewedPhoto, setLastViewedPhoto];
};

const ListingImageGallery: FC<Props> = ({ onClose, isShowModal, listingImgs }) => {
  const images: Image[] = listingImgs?.map((photo: any, index: number) => ({
    id: index,
    url: photo,
  }));

  const [searchParams] = useSearchParams();
  const photoId = searchParams.get("photoId");
  const navigate = useNavigate();
  const [lastViewedPhoto, setLastViewedPhoto] = useLastViewedPhoto();
  const lastViewedPhotoRef = useRef<HTMLDivElement>(null);
  const thisPathname = useLocation().pathname;

  useEffect(() => {
    if (lastViewedPhoto && !photoId) {
      lastViewedPhotoRef.current?.scrollIntoView({ block: "center" });
      setLastViewedPhoto(null);
    }
  }, [photoId, lastViewedPhoto, setLastViewedPhoto]);

const handleClose = useCallback(() => {
  if (onClose) {
    onClose();
  }
  
  // Set last viewed photo and clear 'photoId' from search params
  setLastViewedPhoto(photoId);
  const params = new URLSearchParams(window.location.search);
  params.delete("photoId");

  // Use a single `/` if the path already includes one
  const newPath = `${thisPathname}?${params.toString()}`;
  navigate(newPath, { replace: true });
}, [onClose, setLastViewedPhoto, photoId, thisPathname, navigate]);

  const handleImageClick = useCallback(
    (id: number) => {
      const newPathname = getNewParam({ value: id });
      navigate(`${thisPathname}/?${newPathname}`);
    },
    [navigate, thisPathname]
  );

  const renderContent = () => (
    <div>
      {photoId && (
        <Modal
          images={images}
          onClose={() => {
            setLastViewedPhoto(photoId); // No error here
            const params = new URLSearchParams(window.location.search);
            params.delete("photoId");
            navigate(`${thisPathname}/?${params.toString()}`);
          }}
        />
      )}
      <div className=" columns-1 gap-4 sm:columns-2 xl:columns-3">
        {images?.map(({ id, url }) => (
          <div
            key={id}
            onClick={() => handleImageClick(id)}
            ref={id === Number(lastViewedPhoto) ? lastViewedPhotoRef : null}
            className="group relative mb-5 block w-full cursor-zoom-in"
          >
            <img
              alt="bbe listing gallery"
              className="transform rounded-lg brightness-90 transition will-change-auto group-hover:brightness-110 focus:outline-none"
              style={{ transform: "translate3d(0, 0, 0)" }}
              src={url}
              width={720}
              height={480}
              sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 350px"
            />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Transition appear show={isShowModal} as={Fragment}>
      <Dialog as="div" className="relative z-40" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-white" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="sticky z-10 top-0 p-4 xl:px-10 flex items-center justify-between bg-white">
            <button
              className="focus:outline-none w-10 h-10 rounded-full flex items-center justify-center hover:bg-neutral-100"
              onClick={handleClose}
            >
              <ArrowSmallLeftIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="flex min-h-full items-start justify-center sm:p-4 pt-0 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-5"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-5"
            >
              <Dialog.Panel className="w-full max-w-screen-lg mx-auto transform p-4 pt-0 text-left transition-all flex items-start ">
                {renderContent()}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ListingImageGallery;